import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import type { EsnIconName } from '../../../shared/icons';
import {
  esnTourPlacement,
  type EsnTourDot,
  type EsnTourPointer,
} from '../../../shared/types/support';
import { ButtonComponent } from '../../atoms/button/button.component';

let nextId = 0;

/**
 * The first-run walkthrough callout: where the tour is (progress dots), what this step is
 * about, and Back / Next / Skip. Optionally pinned next to the element the step explains.
 */
@Component({
  selector: 'esn-tour-callout',
  imports: [ButtonComponent],
  templateUrl: './tour-callout.component.html',
  styleUrl: './tour-callout.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.esn-tour-callout-host--anchored]': 'coords() !== null',
    '[style.top.px]': 'coords()?.top ?? null',
    '[style.left.px]': 'coords()?.left ?? null',
  },
})
export class TourCalloutComponent {
  /**
   * 1-based step and the tour length; they drive the dots and which footer controls show.
   */
  readonly step = input(1);
  readonly total = input(8);
  readonly heading = input('');
  readonly body = input('');

  /**
   * Callout edge carrying the arrow; when anchored, the callout sits on the opposite side.
   */
  readonly pointer = input<EsnTourPointer>('none');

  /**
   * Element this step explains; the callout is then positioned next to it (`position: fixed`).
   */
  readonly anchor = input<HTMLElement | ElementRef<HTMLElement> | null>(null);

  readonly nextLabel = input('');
  readonly backLabel = input('');
  readonly skipLabel = input('');

  /**
   * Primary label and optional glyph on the last step; falls back to `nextLabel`.
   */
  readonly finishLabel = input('');
  readonly finishIcon = input<EsnIconName | null>(null);

  /**
   * Screen-reader progress text; defaults to "n / total".
   */
  readonly progressLabel = input('');

  readonly next = output<void>();
  readonly back = output<void>();
  readonly skip = output<void>();
  readonly finish = output<void>();

  protected readonly titleId = `esn-tour-callout-${nextId++}`;
  protected readonly bodyId = `${this.titleId}-body`;

  protected readonly count = computed(() => Math.max(1, Math.round(this.total())));
  protected readonly current = computed(() => Math.min(Math.max(1, Math.round(this.step())), this.count()));
  protected readonly isLast = computed(() => this.current() === this.count());

  /**
   * Back shows between the first and the last step, as in Figma (Step 2–7 of 8).
   */
  protected readonly showBack = computed(() => this.current() > 1 && !this.isLast());
  protected readonly primaryLabel = computed(() => (this.isLast() && this.finishLabel()) || this.nextLabel());
  protected readonly primaryIcon = computed(() => (this.isLast() ? this.finishIcon() : null));
  protected readonly srProgress = computed(() => this.progressLabel() || `${this.current()} / ${this.count()}`);

  protected readonly dots = computed<EsnTourDot[]>(() =>
    Array.from({ length: this.count() }, (_, i) =>
      i + 1 < this.current() ? 'done' : i + 1 === this.current() ? 'current' : 'todo'));

  protected readonly coords = signal<{ top: number; left: number } | null>(null);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    effect((onCleanup) => {
      const raw = this.anchor();
      const target = raw instanceof ElementRef ? raw.nativeElement : raw;
      const pointer = this.pointer();
      if (!target || typeof window === 'undefined') {
        this.coords.set(null);
        return;
      }

      let frame = 0;
      const update = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.place(target, pointer));
      };
      const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
      resize?.observe(this.host.nativeElement);
      resize?.observe(target);
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      update();

      onCleanup(() => {
        cancelAnimationFrame(frame);
        resize?.disconnect();
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
      });
    });
  }

  protected primary(): void {
    if (this.isLast()) this.finish.emit();
    else this.next.emit();
  }

  private place(target: HTMLElement, pointer: EsnTourPointer): void {
    const own = this.host.nativeElement.getBoundingClientRect();
    this.coords.set(esnTourPlacement(target.getBoundingClientRect(), own, pointer, {
      width: window.innerWidth,
      height: window.innerHeight,
    }));
  }
}
